import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { OfflineProvider } from "@/contexts/OfflineContext";
import { Toaster } from "@/components/ui/sonner";
import { registerSW } from "@/lib/pwa";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Terrain from "@/pages/Terrain";
import Pipeline from "@/pages/Pipeline";
import ProspectDetail from "@/pages/ProspectDetail";
import VisitMode from "@/pages/VisitMode";
import PhoneMode from "@/pages/PhoneMode";
import SocialMode from "@/pages/SocialMode";
// Campaigns disabled — routes redirect to /dashboard
import Sprint from "@/pages/Sprint";
import WeeklyReport from "@/pages/WeeklyReport";
import ImportPage from "@/pages/ImportPage";
import Settings from "@/pages/Settings";
import Trouver from "@/pages/Trouver";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Team from "@/pages/Team";
import ManagerDashboard from "@/pages/ManagerDashboard";
import Admin from "@/pages/Admin";
import AdminOnboardingInsights from "@/pages/AdminOnboardingInsights";
import AdminFrontendErrors from "@/pages/AdminFrontendErrors";
import Rgpd from "@/pages/Rgpd";
import BusinessCard from "@/pages/BusinessCard";
import PublicCard from "@/pages/PublicCard";
import Courses from "@/pages/Courses";
import CalendarSync from "@/pages/CalendarSync";
import VisitReport from "@/pages/VisitReport";
import Privacy from "@/pages/Privacy";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import SecuritySettings from "@/pages/SecuritySettings";
import EmailSettings from "@/pages/EmailSettings";
import EmailsSent from "@/pages/EmailsSent";
import EmailPendingBanner from "@/components/EmailPendingBanner";
import CookieBanner from "@/components/CookieBanner";
import { AnimatePresence, motion } from "framer-motion";

import "leaflet/dist/leaflet.css";
import "@/index.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!user.onboarded && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id (Emergent Google Auth)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      >
      <Routes location={location} key={location.pathname}>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/2fa-setup" element={<Protected><TwoFactorSetup /></Protected>} />
      <Route path="/security" element={<Protected><SecuritySettings /></Protected>} />
      <Route path="/emails/settings" element={<Protected><EmailSettings /></Protected>} />
      <Route path="/emails/history" element={<Protected><EmailsSent /></Protected>} />
      <Route path="/c/:id" element={<PublicCard />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/terrain" element={<Protected><Terrain /></Protected>} />
      <Route path="/radar" element={<Navigate to="/terrain?tab=carte" replace />} />
      <Route path="/tour" element={<Navigate to="/terrain?tab=tournee" replace />} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/prospect/:id" element={<Protected><ProspectDetail /></Protected>} />
      <Route path="/visit/:id" element={<Protected><VisitMode /></Protected>} />
      <Route path="/phone/:id" element={<Protected><PhoneMode /></Protected>} />
      <Route path="/social/:id" element={<Protected><SocialMode /></Protected>} />
      <Route path="/campaigns" element={<Navigate to="/dashboard" replace />} />
      <Route path="/campaigns/:id" element={<Navigate to="/dashboard" replace />} />
      <Route path="/sprint" element={<Protected><Sprint /></Protected>} />
      <Route path="/report" element={<Protected><WeeklyReport /></Protected>} />
      <Route path="/import" element={<Protected><ImportPage /></Protected>} />
      <Route path="/trouver" element={<Protected><Trouver /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/team/dashboard" element={<Protected><ManagerDashboard /></Protected>} />
      <Route path="/prosperrboss" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/onboarding/insights" element={<Protected><AdminOnboardingInsights /></Protected>} />
      <Route path="/admin/frontend/errors" element={<Protected><AdminFrontendErrors /></Protected>} />
      <Route path="/visit-report/:id" element={<Protected><VisitReport /></Protected>} />
      <Route path="/prospects-list" element={<Navigate to="/pipeline?view=list" replace />} />
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/c7k-control-prosperr-2026" element={<Navigate to="/dashboard" replace />} />
      <Route path="/rgpd" element={<Protected><Rgpd /></Protected>} />
      <Route path="/card" element={<Protected><BusinessCard /></Protected>} />
      <Route path="/courses" element={<Protected><Courses /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarSync /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  React.useEffect(() => { registerSW(); }, []);
  return (
    <BrowserRouter>
      <BrandProvider>
        <AuthProvider>
          <OfflineProvider>
            <AppRouter />
            <EmailPendingBanner />
            <CookieBanner />
            <Toaster position="top-center" richColors />
          </OfflineProvider>
        </AuthProvider>
      </BrandProvider>
    </BrowserRouter>
  );
}
